import clsx from 'clsx'
import { rem } from 'polished'
import { memo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'

const StyledUserAvatarContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(props) => props.theme.borderRadius.round};
  flex-shrink: 0;

  width: var(--avatar-size);
  height: var(--avatar-size);
  background-color: var(--avatar-bg-colour);
  border: 1px solid var(--avatar-border-colour);
  fill: currentColor;
  color: var(--avatar-border-colour);

  &.variant-primary {
    &.kind-default {
      --avatar-bg-colour: ${(props) =>
        props.theme.palette.brand.primary.normal};
      --avatar-border-colour: ${(props) =>
        props.theme.palette.neutral.default.eight};

      &.disabled {
        --avatar-bg-colour: ${(props) =>
          props.theme.palette.neutral.default.four};
      }
    }

    &.kind-dark {
      --avatar-bg-colour: ${(props) =>
        props.theme.palette.neutral.default.eight};
      --avatar-border-colour: ${(props) =>
        props.theme.palette.neutral.control.black100};

      &.disabled {
        --avatar-border-colour: ${(props) =>
          props.theme.palette.neutral.default.four};
      }
    }
  }

  &.variant-secondary {
    &.kind-default {
      --avatar-bg-colour: ${(props) =>
        props.theme.palette.neutral.default.eight};
      --avatar-border-colour: ${(props) =>
        props.theme.palette.brand.primary.normal};

      &.disabled {
        --avatar-border-colour: ${(props) =>
          props.theme.palette.neutral.default.four};
      }
    }

    &.kind-dark {
      --avatar-bg-colour: ${(props) =>
        props.theme.palette.neutral.control.black100};
      --avatar-border-colour: ${(props) =>
        props.theme.palette.neutral.default.eight};

      &.disabled {
        --avatar-border-colour: ${(props) =>
          props.theme.palette.neutral.default.four};
      }
    }
  }

  &.size-small {
    --avatar-size: ${rem(24)};
  }

  &.size-medium {
    --avatar-size: ${rem(28)};
  }

  &.size-large {
    --avatar-size: ${rem(36)};
  }

  &.size-xxl {
    --avatar-size: ${rem(64)};
  }

  .avatar-text {
    line-height: 1em;
  }
`

const StyledUserImage = styled.img`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(props) => props.theme.borderRadius.round};

  width: var(--avatar-size);
  height: var(--avatar-size);
  background-color: var(--avatar-bg-colour);
  border: 1px solid var(--avatar-border-colour);
  fill: currentColor;
  color: var(--avatar-border-colour);

  &.size-small {
    --avatar-size: ${rem(24)};
  }

  &.size-medium {
    --avatar-size: ${rem(28)};
  }

  &.size-large {
    --avatar-size: ${rem(36)};
  }

  &.size-xxl {
    --avatar-size: ${rem(64)};
  }
`
// const Notification = styled.div`
//   position: absolute;
//   top: ${rem(4)};
//   right: ${rem(4)};
//   background-color: ${props => props.theme.palette.highlight.secondary.normal};
// `

export const USER_AVATAR_KINDS = ['default', 'dark'] as const
export type UserAvatarKind = (typeof USER_AVATAR_KINDS)[number]

export const USER_AVATAR_VARIANTS = ['primary', 'secondary'] as const
export type UserAvatarVariant = (typeof USER_AVATAR_VARIANTS)[number]

export const USER_AVATAR_CONTENT = ['user', 'count'] as const
export type UserAvatarContent = (typeof USER_AVATAR_CONTENT)[number]

export const USER_AVATAR_SIZES = ['small', 'medium', 'large', 'xxl'] as const
export type UserAvatarSize = (typeof USER_AVATAR_SIZES)[number]

interface Props {
  size?: UserAvatarSize
  content?: UserAvatarContent
  kind?: UserAvatarKind
  variant: UserAvatarVariant
  disabled?: boolean
  // notification?: boolean
  firstName?: string
  lastName?: string
  count?: number
  imageSrc?: string
}

function UserAvatar({
  content = 'user',
  count,
  disabled,
  firstName,
  lastName,
  size = 'medium',
  variant,
  kind = 'default',
  imageSrc,
}: Props) {
  const intl = useIntl()
  const Text = size === 'small' ? Caption : BodyText
  const textVariant = size === 'large' ? 'medium' : 'small'
  const iconSize = size === 'large' ? 20 : 16

  return (
    <StyledUserAvatarContainer
      role="presentation"
      className={clsx(
        `size-${size}`,
        `variant-${variant}`,
        {
          disabled,
        },
        `kind-${kind}`,
      )}
    >
      {content === 'user' && (
        <>
          {imageSrc && (
            <StyledUserImage
              src={imageSrc}
              alt={intl.formatMessage({
                id: 'CaMo9brF-NHLzbsbi6Ma',
                defaultMessage: 'user',
                description: 'Alt text for user avatar image in trip planner sharing',
              })}
            />
          )}
          {!imageSrc && firstName && lastName && (
            <Text variant={textVariant} weight="bold" className="avatar-text">
              {(firstName[0] + lastName[0]).toUpperCase()}
            </Text>
          )}
          {!imageSrc && !(firstName && lastName) && (
            <LineUserIcon size={iconSize} />
          )}
        </>
      )}
      {content === 'count' && (
        <Text variant={textVariant} weight="bold" className="avatar-text">
          {intl.formatMessage(
            {
              id: '5uYVRbbM7xkGFTYHPMSY',
              defaultMessage: '+{count}',
              description: 'Overflow count indicator on user avatar showing additional users',
            },
            { count },
          )}
        </Text>
      )}
    </StyledUserAvatarContainer>
  )
}

export default memo(UserAvatar)
